/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import CustomLink from "components/Links/CustomLink";

import MoreButton from "components/CustomButtons/MoreButton";
import ImageBadge from "components/Badge/ImageBadge.js";

import Button from "components/CustomButtons/Button";
import dayjs from "dayjs";

import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";

import useTranslation from "next-translate/useTranslation";
import LinkBadge from "components/LinkBadge/LinkBadge";
import ProductAvatar from "components/ProductAvatar/ProductAvatar";
import LatestModelBadge from "components/Badge/LatestModelBadge";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpertReviewScoreBadge from "components/Badge/ExpertReviewScoreBadge";

import { useRenders } from "modules/utils";

import Paper from "@mui/material/Paper";

import {
  FAMILY_PRODUCTS_ID,
  MONTH_YEAR_FORMAT,
} from "settings/global-website-config";

import { grayColor } from "../../assets/jss/nextjs-material-kit-pro";
import { getDisplayPrice } from "../../pages-sections/product/ProductInUseSection";

const useStyles = makeStyles()((theme) => ({
  previousButton: {
    position: "absolute",
    zIndex: 100,
    padding: "5px 8px",
    backgroundColor: grayColor[2],
    margin: "10px",
  },

  previousButtonContainer: {
    display: "flex",
    justifyContent: "center",
    minHeight: "50px",
    position: "relative",
  },
  productTitleContainer: {
    display: "inline-block",
    paddingRight: "4px",
    "& > :nth-of-type(2n)": {
      color: "red",
    },
  },
  overlayCircularProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  timelineItem: {
    [theme.breakpoints.down("sm")]: {
      // display: 'unset',
    },
  },
  timelineContainer: {
    padding: "0px",
    margin: "0px -16px 0px -16px",
  },
  currentItemBorder: {
    border: `1px solid ${theme.palette.secondary.main}`,
  },
  timelineDate: {
    color: grayColor[0],
  },
  viewingBadge: { margin: "2px" },
  timelineItemContainer: {
    padding: "6px 16px",
    display: "inline-block !important",
  },
  timelineItemContainerOuter: {
    padding: "0px 16px",
  },
  latestModelContainer: {
    display: "inline-grid",
  },
}));

const FamilyTimeline = (props) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const {
    currentProduct,
    family,
    type,
    handleClick,
    setTimelineOnlyQuery,
    loading,
    state,
    dealsState,
  } = props;

  const { products, uri, termName: familyName } = family;

  useRenders("timeline");
  const fetchMore = () => {
    setTimelineOnlyQuery(true);
    handleClick("previous", {
      uri,
      id: family.id,
      canUpdateFromServer: true,
      bypassComparisonBlockUpdate: true,
    });
  };
  const productsToUse = products.nodes;

  return (
    <div id={FAMILY_PRODUCTS_ID}>
      <Timeline
        position="alternate"
        classes={{
          root: classes.timelineContainer,
          alignLeft: classes.leftTimeline,
        }}
      >
        {productsToUse.map((product, index) => {
          const deals =
            dealsState?.sexDeals?.[currentProduct.databaseId]?.deals;

          const displayPrice = getDisplayPrice(
            product,
            state,
            deals,
            "familyProductDeals"
          );

          return (
            <TimelineItem
              key={product.id}
              classes={{ root: classes.timelineItem }}
            >
              <TimelineOppositeContent className={classes.timelineDate}>
                {product.productDetails.released &&
                  dayjs(product.productDetails.released).format(
                    MONTH_YEAR_FORMAT
                  )}
              </TimelineOppositeContent>
              {
                <TimelineSeparator>
                  <ProductAvatar
                    product={product}
                    currentProduct={currentProduct}
                  />
                  {productsToUse.length !== index + 1 && <TimelineConnector />}
                </TimelineSeparator>
              }
              <TimelineContent
                classes={{ root: classes.timelineItemContainerOuter }}
              >
                <Paper
                  variant="outlined"
                  classes={{
                    outlined:
                      product.id === currentProduct.id
                        ? `${classes.timelineItemContainer} ${classes.currentItemBorder}`
                        : classes.timelineItemContainer,
                  }}
                >
                  <div className={classes.productTitleContainer}>
                    {product.id === currentProduct.id ? (
                      <span className={classes.title}>
                        {product.noBrandTitle}
                      </span>
                    ) : (
                      <CustomLink href={product.uri}>
                        {product.noBrandTitle}
                      </CustomLink>
                    )}
                    {displayPrice && <> {displayPrice}</>}
                  </div>

                  <ExpertReviewScoreBadge
                    reviews={product.productReviews}
                    showScore
                    hideHeader
                    hideDetails
                    textCenter
                    showLoadingSkeleton
                    type={type}
                    href={product.uri}
                  />

                  {product.latestModelDetails?.isLatestModel && (
                    <div className={classes.latestModelContainer}>
                      <LatestModelBadge noMargin />
                    </div>
                  )}

                  <div>
                    {product.characteristics.edges
                      .find(
                        (edge) => edge.characteristic.node.name === "feature"
                      )
                      ?.terms.edges.map((feature) => (
                        <LinkBadge
                          key={`${product.id}-${feature.node.name}`}
                          name={feature.node.name}
                          href={feature.uri}
                        />
                      ))}
                  </div>
                  {product.id === currentProduct.id && (
                    <ImageBadge
                      label={t("product:viewing")}
                      customClasses={classes.viewingBadge}
                    />
                  )}
                </Paper>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      {family.hasPreviousPage && (
        <div className={classes.previousButtonContainer}>
          <Button
            classes={{
              root: classes.previousButton,
            }}
            size="medium"
            link
            disabled={loading}
            onClick={() => fetchMore()}
            startIcon={<ExpandMoreIcon />}
          >
            <>
              {t("product:viewOlder")}
              {loading && (
                <CircularProgress
                  color="primary"
                  className={classes.overlayCircularProgress}
                  size={21}
                />
              )}
            </>
          </Button>
        </div>
      )}

      {currentProduct.__typename !== "Family" && (
        <MoreButton
          show
          uri={uri}
          text={t("product:viewFamily", { family: familyName })}
          loading={loading}
        />
      )}
    </div>
  );
};

if (process.env.NODE_ENV !== "production") {
  FamilyTimeline.displayName = "FamilyTimeline";
}

export default React.memo(FamilyTimeline);
